<template>
  <MTooltip
    ref="tab"
    class="w-40 lg:w-32 shrink-0 mr-2 first:ml-2 lg:mr-4 lg:first:ml-0 lg:last:mr-0 lg:pt-6"
    :class="{ expanded: isExpanded }"
    tooltip-class="hidden lg:block"
    toggle-class="h-full"
    placement="top"
  >
    <template #toggle>
      <button
        class="relative bg-white rounded-6 shadow-1 flex flex-col items-center px-1 pt-3 pb-2 h-full w-full border-b-4"
        :class="isActive ? 'active border-black' : 'border-transparent'"
        role="tab"
        :id="tab.type"
        :aria-controls="`${tab.type}-tab`"
        :aria-selected="isActive ? 'true' : 'false'"
        @click="$emit('click', tab.type)"
      >
        <strong
          v-if="tab.label"
          class="hidden lg:inline-block absolute left-0 bottom-full max-w-full bg-green-800 text-white px-2 py-1 rounded-t-6 text-12 uppercase truncate"
        >
          {{ $t(`label.${tab.label}`) }}
        </strong>
        <img v-if="tabIcon" class="icon icon-md mb-2" :src="tabIcon" alt="" />
        <div class="text-12 w-full truncate">
          <template v-if="tab.type === 'broker'">{{ $t('transfer') }}</template>
          <template v-else>{{ $t('transfer_to') }}</template>
        </div>
        <div
          class="font-semibold leading-tight w-full lg:truncate"
          :class="{ 'lg:hidden': !isExpanded }"
        >
          {{ $t(`tabs.title_${tab.type}`) }}
        </div>
        <div
          class="font-semibold leading-tight w-full truncate hidden"
          :class="{ 'lg:block': !isExpanded }"
        >
          {{ $t(`tabs.short_title_${tab.type}`) }}
        </div>
        <div class="w-full truncate">{{ receivedAmount }}</div>
        <div
          v-if="tab.label"
          class="lg:hidden text-12 uppercase text-green-800 font-bold w-full truncate"
        >
          {{ $t(`label.${tab.label}`) }}
        </div>
      </button>
    </template>
    <template #content>
      <div class="text-14">
        {{ $t(`tabs.description_${tab.type}`) }}
      </div>
    </template>
  </MTooltip>
</template>

<script lang="ts" setup>
import MTooltip from '@core/components/m-tooltip/index.vue'
import type { Tab as TabType } from '../types'

const props = defineProps<{
  tab: TabType
  currencyTo: string
  isActive?: boolean
  isExpanded?: boolean
}>()

defineEmits(['click'])

const tabRef = useTemplateRef<InstanceType<typeof MTooltip>>('tab')

const { $lang, $t } = useNuxtApp()

onMounted(() => {
  scrollToTab()
})

watch(toRef(props, 'isActive'), () => {
  scrollToTab()
})

function scrollToTab() {
  if (props.isActive) {
    if (tabRef.value?.$el) {
      // finalOffset = tab offset based on window + tab width/2 - window width/2
      const tabElement = tabRef.value.$el
      const finalOffset =
        tabElement.offsetLeft + tabElement.getBoundingClientRect().width / 2 - window.innerWidth / 2
      tabElement.parentElement?.scrollTo({
        left: finalOffset,
      })
    }
  }
}

const tabIcon = computed(() => {
  return getPaymentIcon(props.tab.type)
})

const receivedAmount = computed(() => {
  return formatMoney(props.tab.amount, props.currencyTo, $lang, 0)
})
</script>

<style scoped>
@screen lg {
  .expanded {
    @apply w-full;
    max-width: 17rem;
  }
}
.active:before {
  content: '';
  transform: rotateZ(-45deg) skew(20deg, 20deg);
  @apply absolute bg-black w-5 h-5 -bottom-3 -z-1;
}
</style>
